import { useMemo, useState } from "react";
import { AlertTriangle, BadgeCheck, ChevronDown, ChevronUp, FileSearch, ShieldAlert, Sparkles } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import type { NormalizedDocumentIntelligence, ValidationStatus } from "@/lib/document-intelligence";

type PanelMode = "compact" | "full";

interface DocumentIntelligencePanelProps {
  intelligence: NormalizedDocumentIntelligence;
  mode?: PanelMode;
  defaultExpanded?: boolean;
  title?: string;
}

interface InsightItem {
  title: string;
  detail?: string;
}

interface ActionableGuidance {
  whyThisMatters: string;
  strengthens: string;
  weakens: string;
  attention: string;
  nextStep: string;
}

const validationTone: Record<Exclude<ValidationStatus, null>, string> = {
  strong: "border-green-500/30 bg-green-500/10 text-green-500",
  partial: "border-amber-500/30 bg-amber-500/10 text-amber-500",
  weak: "border-destructive/30 bg-destructive/10 text-destructive",
  failed: "border-destructive/30 bg-destructive/10 text-destructive",
};

function prettifyLabel(value: string) {
  return value.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
}

function toInsightItem(value: unknown): InsightItem | null {
  if (!value) return null;
  if (typeof value === "string") return { title: value };
  if (typeof value === "number" || typeof value === "boolean") return { title: String(value) };
  if (typeof value === "object" && !Array.isArray(value)) {
    const source = value as Record<string, unknown>;
    const title = [source.title, source.label, source.risk, source.adjustment, source.summary, source.issue, source.name]
      .find((item) => typeof item === "string" && item.trim().length > 0);
    const detail = [source.detail, source.reason, source.rationale, source.description, source.impact, source.note, source.value]
      .find((item) => typeof item === "string" && item.trim().length > 0);
    if (typeof title === "string") return { title, detail: typeof detail === "string" ? detail : undefined };
    const entries = Object.entries(source).filter(([, item]) => item != null && item !== "");
    if (entries.length === 0) return null;
    const [firstKey, firstValue] = entries[0];
    return {
      title: `${prettifyLabel(firstKey)}: ${String(firstValue)}`,
      detail: entries.length > 1 ? entries.slice(1, 3).map(([key, item]) => `${prettifyLabel(key)}: ${String(item)}`).join(" • ") : undefined,
    };
  }
  return null;
}

function getConfidenceTone(score: number) {
  if (score >= 80) return "border-green-500/30 bg-green-500/10 text-green-500";
  if (score >= 55) return "border-amber-500/30 bg-amber-500/10 text-amber-500";
  return "border-destructive/30 bg-destructive/10 text-destructive";
}

function getValidationLabel(status: ValidationStatus | null) {
  return status ? prettifyLabel(status) : "Unscored";
}

function getDocumentLabel(intelligence: NormalizedDocumentIntelligence) {
  return intelligence.document_lane || intelligence.doc_type;
}

function formatMappingValue(value: unknown) {
  if (value == null || value === "") return "Not mapped";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) return value.map((item) => (typeof item === "string" ? item : String(item))).slice(0, 3).join(" • ");
  if (typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>).slice(0, 3);
    return entries.map(([key, item]) => `${prettifyLabel(key)}: ${String(item)}`).join(" • ");
  }
  return String(value);
}

function getLaneKey(intelligence: NormalizedDocumentIntelligence) {
  const lane = `${intelligence.document_lane || intelligence.doc_type || ""}`.toLowerCase();
  if (lane.includes("rent")) return "rent_roll";
  if (lane.includes("t12") || lane.includes("trailing")) return "t12";
  return "supporting_document";
}

function getDefaultGuidance(intelligence: NormalizedDocumentIntelligence): ActionableGuidance {
  switch (getLaneKey(intelligence)) {
    case "rent_roll":
      return {
        whyThisMatters: "This file anchors in-place revenue quality, occupancy credibility, and unit-level pricing for the deal.",
        strengthens: "It strengthens current rent, occupancy, and unit mix assumptions when the unit-level picture is internally consistent.",
        weakens: "It weakens trust when occupied units, concessions, delinquency signals, or asking-to-in-place gaps do not reconcile cleanly.",
        attention: "Pay attention to vacancy concentration, concessions, lease trade-out, and whether in-place rents support the revenue story.",
        nextStep: "Verify rent assumptions against comps and confirm occupancy and concession claims before leaning on the rent story.",
      };
    case "t12":
      return {
        whyThisMatters: "This file shows whether the property’s trailing operations support the deal’s income and expense story.",
        strengthens: "It strengthens NOI, expense ratio, and collections assumptions when operating performance is stable and explainable.",
        weakens: "It weakens trust when expenses appear understated, one-time items distort results, or the operating history looks incomplete.",
        attention: "Pay attention to repairs, payroll, taxes, insurance, utilities, bad debt, and any trend that makes projected NOI look fragile.",
        nextStep: "Confirm expense realism and normalize trailing operating performance before relying on projected NOI.",
      };
    default:
      return {
        whyThisMatters: "This file adds supporting evidence around assumptions, exclusions, and risks that the core operating documents may not fully explain.",
        strengthens: "It strengthens confidence when third-party findings, scope details, budgets, terms, or market evidence clearly support the deal thesis.",
        weakens: "It weakens trust when exclusions, missing sections, or unsupported claims leave important risks unresolved.",
        attention: "Pay attention to exclusions, contingencies, timing, and any item that could materially change CapEx, financing, or execution risk.",
        nextStep: "Review missing evidence and unresolved exclusions before relying on this output in underwriting.",
      };
  }
}

function getRecommendedNextStep(fallback: string, risk?: InsightItem | null, adjustment?: InsightItem | null, hasWeakSignal?: boolean) {
  const combined = `${risk?.title || ""} ${risk?.detail || ""} ${adjustment?.title || ""} ${adjustment?.detail || ""}`.toLowerCase();

  if (hasWeakSignal || /missing|incomplete|weak|unsupported|unclear|thin evidence/.test(combined)) {
    return "Review missing evidence before relying on this output in the deal decision.";
  }
  if (/rent|lease|loss to lease|comp|concession/.test(combined)) {
    return "Verify rent assumptions against comps, in-place rents, and concession trends before finalizing revenue upside.";
  }
  if (/occup|vacan|delinquen|collections/.test(combined)) {
    return "Validate occupancy and collections claims against unit-level evidence before underwriting to the current revenue base.";
  }
  if (/expense|tax|insurance|utility|repair|noi|opex/.test(combined)) {
    return "Confirm expense realism and normalize any unusual operating items before relying on the modeled NOI.";
  }
  if (/capex|roof|hvac|plumbing|electrical|inspection|deferred maintenance|scope|budget/.test(combined)) {
    return "Review CapEx scope, exclusions, and deferred maintenance exposure more closely before locking renovation assumptions.";
  }
  if (/loan|lender|debt|interest|rate|amort/.test(combined)) {
    return "Review lender terms and confirm debt assumptions before treating the financing plan as reliable.";
  }

  return fallback;
}

function buildActionableGuidance(
  intelligence: NormalizedDocumentIntelligence,
  risk: InsightItem | null,
  adjustment: InsightItem | null,
  hasWeakSignal: boolean,
): ActionableGuidance {
  const baseline = getDefaultGuidance(intelligence);
  const attention = risk?.title
    ? `Pay attention to ${risk.title.charAt(0).toLowerCase()}${risk.title.slice(1)}${risk.detail ? ` — ${risk.detail}` : ""}.`
    : baseline.attention;

  return {
    whyThisMatters: baseline.whyThisMatters,
    strengthens: adjustment?.title
      ? `${baseline.strengthens} Current output suggests the model may need to reflect ${adjustment.title.charAt(0).toLowerCase()}${adjustment.title.slice(1)}.`
      : baseline.strengthens,
    weakens: hasWeakSignal
      ? "Trust is currently limited because validation or confidence is weak, so this should be treated as directional until corroborated."
      : baseline.weakens,
    attention,
    nextStep: getRecommendedNextStep(baseline.nextStep, risk, adjustment, hasWeakSignal),
  };
}

export function DocumentIntelligencePanel({
  intelligence,
  mode = "full",
  defaultExpanded = false,
  title = "Document Intelligence",
}: DocumentIntelligencePanelProps) {
  const [open, setOpen] = useState(defaultExpanded);

  const riskItems = useMemo(
    () => intelligence.risk_flags.map(toInsightItem).filter((item): item is InsightItem => Boolean(item)),
    [intelligence.risk_flags],
  );

  const adjustmentItems = useMemo(
    () => intelligence.recommended_underwriting_adjustments.map(toInsightItem).filter((item): item is InsightItem => Boolean(item)),
    [intelligence.recommended_underwriting_adjustments],
  );

  const evidenceItems = useMemo(
    () => intelligence.evidence_log.map(toInsightItem).filter((item): item is InsightItem => Boolean(item)),
    [intelligence.evidence_log],
  );

  const mappingEntries = useMemo(() => Object.entries(intelligence.field_mapping), [intelligence.field_mapping]);
  const previewRisk = riskItems[0];
  const previewAdjustment = adjustmentItems[0];
  const hasWeakSignal = intelligence.validation_status === "weak" || intelligence.validation_status === "failed" || intelligence.confidence.score < 55;
  const confidenceTone = getConfidenceTone(intelligence.confidence.score);
  const validationToneClass = intelligence.validation_status ? validationTone[intelligence.validation_status] : "border-border bg-muted text-muted-foreground";
  const previewInterpretation = intelligence.executive_interpretation || "No executive interpretation generated.";
  const actionableGuidance = useMemo(
    () => buildActionableGuidance(intelligence, previewRisk, previewAdjustment, hasWeakSignal),
    [hasWeakSignal, intelligence, previewAdjustment, previewRisk],
  );

  const summaryContent = (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="outline" className="border-border bg-muted/50 eyebrow text-muted-foreground">
          {prettifyLabel(getDocumentLabel(intelligence))}
        </Badge>
        {intelligence.document_subtype && (
          <Badge variant="outline" className="border-border bg-muted/50 text-2xs uppercase tracking-[0.14em] text-muted-foreground">
            {prettifyLabel(intelligence.document_subtype)}
          </Badge>
        )}
        <Badge variant="outline" className={confidenceTone}>
          Confidence {intelligence.confidence.score}/100
        </Badge>
        <Badge variant="outline" className={validationToneClass}>
          {getValidationLabel(intelligence.validation_status)}
        </Badge>
      </div>

      <div className="grid gap-3 md:grid-cols-3">
        <div className="rounded-md border border-border bg-muted/30 p-3">
          <p className="eyebrow text-muted-foreground">Top Risk</p>
          <p className="mt-2 text-sm text-foreground">{previewRisk?.title || "No risk flags surfaced."}</p>
          {previewRisk?.detail && <p className="mt-1 text-xs text-muted-foreground">{previewRisk.detail}</p>}
        </div>
        <div className="rounded-md border border-border bg-muted/30 p-3">
          <p className="eyebrow text-muted-foreground">Top Adjustment</p>
          <p className="mt-2 text-sm text-foreground">{previewAdjustment?.title || "No underwriting adjustment suggested."}</p>
          {previewAdjustment?.detail && <p className="mt-1 text-xs text-muted-foreground">{previewAdjustment.detail}</p>}
        </div>
        <div className="rounded-md border border-border bg-muted/30 p-3">
          <p className="eyebrow text-muted-foreground">Mapped Fields</p>
          <p className="mt-2 text-sm text-foreground">{mappingEntries.length} field{mappingEntries.length === 1 ? "" : "s"} mapped</p>
          <p className="mt-1 text-xs text-muted-foreground">{evidenceItems.length} evidence item{evidenceItems.length === 1 ? "" : "s"} captured</p>
        </div>
      </div>

      <div className={`rounded-md border p-3 ${hasWeakSignal ? "border-amber-500/30 bg-amber-500/10" : "border-border bg-muted/20"}`}>
        <div className="flex items-start gap-2">
          {hasWeakSignal ? <ShieldAlert className="mt-0.5 h-4 w-4 text-amber-500" /> : <BadgeCheck className="mt-0.5 h-4 w-4 text-green-500" />}
          <div className="space-y-1">
            <p className="text-sm text-foreground line-clamp-3">{previewInterpretation}</p>
            {hasWeakSignal && (
              <p className="text-xs text-muted-foreground">
                Treat this intelligence as directional until stronger corroborating documents are uploaded.
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
        <div className="rounded-md border border-border bg-muted/20 p-3">
          <p className="eyebrow text-muted-foreground">Why This Matters</p>
          <div className="mt-2 space-y-2">
            <p className="text-sm text-foreground">{actionableGuidance.whyThisMatters}</p>
            <div className="space-y-1 text-xs text-muted-foreground">
              <p><span className="text-foreground">Strengthens:</span> {actionableGuidance.strengthens}</p>
              <p><span className="text-foreground">Weakens:</span> {actionableGuidance.weakens}</p>
              <p><span className="text-foreground">Pay attention:</span> {actionableGuidance.attention}</p>
            </div>
          </div>
        </div>

        <div className="rounded-md border border-border bg-muted/20 p-3">
          <p className="eyebrow text-muted-foreground">Recommended Next Step</p>
          <p className="mt-2 text-sm text-foreground">{actionableGuidance.nextStep}</p>
          {hasWeakSignal && (
            <p className="mt-2 text-xs text-muted-foreground">
              Confidence remains visible above so users can judge whether this should inform a decision now or after more support is uploaded.
            </p>
          )}
        </div>
      </div>
    </div>
  );

  if (mode === "compact") {
    return summaryContent;
  }

  return (
    <Card className="rounded-lg border-border bg-card shadow-sm">
      <CardHeader className="pb-3">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div>
            <CardTitle className="text-lg">{title}</CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">
              Analyst-grade evidence, trust signals, and underwriting implications from this file.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className={confidenceTone}>{intelligence.confidence.level} confidence</Badge>
            <Badge variant="outline" className={validationToneClass}>{getValidationLabel(intelligence.validation_status)}</Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {summaryContent}

        <Collapsible open={open} onOpenChange={setOpen}>
          <CollapsibleTrigger asChild>
            <Button variant="outline" className="w-full justify-between">
              <span className="flex items-center gap-2">
                <FileSearch className="h-4 w-4" /> View Full Analysis
              </span>
              {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="pt-4">
            <div className="grid gap-4 lg:grid-cols-2">
              <section className="rounded-md border border-border bg-muted/20 p-4">
                <div className="mb-3 flex items-center gap-2">
                  <ShieldAlert className="h-4 w-4 text-muted-foreground" />
                  <h3 className="font-display text-sm font-semibold">Confidence reasoning</h3>
                </div>
                <p className="text-sm text-foreground">
                  {intelligence.confidence.level} trust at {intelligence.confidence.score}/100.
                </p>
                <div className="mt-3 space-y-2">
                  {intelligence.confidence.notes.length > 0 ? (
                    intelligence.confidence.notes.map((note, index) => (
                      <div key={index} className="flex items-start gap-2 text-xs text-muted-foreground">
                        <Sparkles className="mt-0.5 h-3 w-3 shrink-0 text-accent" />
                        <span>{note}</span>
                      </div>
                    ))
                  ) : (
                    <p className="text-xs text-muted-foreground">No additional confidence notes were returned.</p>
                  )}
                </div>
              </section>

              <section className="rounded-md border border-border bg-muted/20 p-4">
                <div className="mb-3 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-muted-foreground" />
                  <h3 className="font-display text-sm font-semibold">Underwriting adjustments</h3>
                </div>
                <div className="space-y-3">
                  {adjustmentItems.length > 0 ? (
                    adjustmentItems.map((item, index) => (
                      <div key={`${item.title}-${index}`} className="rounded-md border border-border bg-card p-3">
                        <p className="text-sm text-foreground">{item.title}</p>
                        {item.detail && <p className="mt-1 text-xs text-muted-foreground">{item.detail}</p>}
                      </div>
                    ))
                  ) : (
                    <p className="text-xs text-muted-foreground">No underwriting adjustments returned for this document.</p>
                  )}
                </div>
              </section>

              <section className="rounded-md border border-border bg-muted/20 p-4 lg:col-span-2">
                <div className="mb-3 flex items-center gap-2">
                  <BadgeCheck className="h-4 w-4 text-muted-foreground" />
                  <h3 className="font-display text-sm font-semibold">Executive interpretation</h3>
                </div>
                <p className="text-sm leading-6 text-foreground">{previewInterpretation}</p>
              </section>

              <section className="rounded-md border border-border bg-muted/20 p-4">
                <div className="mb-3 flex items-center gap-2">
                  <FileSearch className="h-4 w-4 text-muted-foreground" />
                  <h3 className="font-display text-sm font-semibold">Evidence log</h3>
                </div>
                <div className="space-y-3">
                  {evidenceItems.length > 0 ? (
                    evidenceItems.map((item, index) => (
                      <div key={`${item.title}-${index}`} className="rounded-md border border-border bg-card p-3">
                        <p className="text-sm text-foreground">{item.title}</p>
                        {item.detail && <p className="mt-1 text-xs text-muted-foreground">{item.detail}</p>}
                      </div>
                    ))
                  ) : (
                    <p className="text-xs text-muted-foreground">No evidence entries were captured.</p>
                  )}
                </div>
              </section>

              <section className="rounded-md border border-border bg-muted/20 p-4">
                <div className="mb-3 flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-muted-foreground" />
                  <h3 className="font-display text-sm font-semibold">Field mapping</h3>
                </div>
                <div className="space-y-2">
                  {mappingEntries.length > 0 ? (
                    mappingEntries.map(([key, value]) => (
                      <div key={key} className="rounded-md border border-border bg-card p-3">
                        <p className="eyebrow text-muted-foreground">{prettifyLabel(key)}</p>
                        <p className="mt-1 text-sm text-foreground break-words">{formatMappingValue(value)}</p>
                      </div>
                    ))
                  ) : (
                    <p className="text-xs text-muted-foreground">No field mapping was generated.</p>
                  )}
                </div>
              </section>
            </div>
          </CollapsibleContent>
        </Collapsible>
      </CardContent>
    </Card>
  );
}